// ABOUTME: Tab bar of a custom page column on its own DS Paper, full width; only the active tab's content is mounted.
// ABOUTME: Receives the tabs the user can see (LayoutColumn filters them by role); a single tab renders without the bar.

import { Box, Paper, Stack, Tab, Tabs } from '@ds/coolshop';
import { ReactNode, useState } from 'react';

import { CustomPageTab } from '../types';
import { resolveIcon } from '../utils/resolveIcon';

interface ColumnTabsProps {
    /** The tabs the user can see, already filtered by role (see `visibleTabs`). */
    tabs: CustomPageTab[];
    renderTab: (tab: CustomPageTab) => ReactNode;
}

export function ColumnTabs({ tabs: shown, renderTab }: ColumnTabsProps) {
    const [selectedId, setSelectedId] = useState<string>();

    if (shown.length === 0) return null;
    const active = shown.find(t => t.id === selectedId) ?? shown[0];
    if (shown.length === 1) return <>{renderTab(active)}</>;

    return (
        <Stack gap={2} width="100%">
            {/* Only the bar sits on the paper; the tab content stays on the page background. */}
            <Paper elevation={2}>
                {/* The DS Paper takes no padding: the box gives the bar room inside the paper. The DS
                    Tabs is 48px tall around 42px tabs aligned to its top, so the bottom padding is
                    6px smaller to leave the same visible gap above and below the tabs. */}
                <Box px={1} pt={1} pb="2px">
                    <Tabs
                        value={active.id}
                        onChange={(_, value: string) => setSelectedId(value)}
                        variant="fullWidth"
                        orientation="horizontal"
                    >
                        {shown.map(t => (
                            <Tab
                                key={t.id}
                                value={t.id}
                                // The DS Tab has no icon prop: the icon travels inside the label.
                                label={
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        {t.icon ? resolveIcon(t.icon) : null}
                                        {t.label}
                                    </Stack>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>
            </Paper>
            {/* Keyed by tab so switching remounts the widgets instead of reusing their state. */}
            <Stack key={active.id} width="100%" role="tabpanel" aria-label={active.label}>
                {renderTab(active)}
            </Stack>
        </Stack>
    );
}
